import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { usePhotoGenerate } from '@genfun/core/hooks/usePhotoGenerate';
import { fetchGallery } from '@genfun/core/api/API';
import {
    POSE_OPTIONS, CLOTHES_OPTIONS, LOCATION_OPTIONS, CLOTHES_IMAGES, LOCATION_IMAGES,
    POSE_PREVIEWS, CLOTHES_PREVIEWS, LOCATION_PREVIEWS, GIRL_PRESETS,
} from '../data/photoOptions';
import { isPresetCompatible, resetFormRef, applyPreset, nextGirlStage, firstGirlStage } from '../utils/photoForm';
import { useSession } from '../utils/session';
import { downloadFile } from '../utils/download';
import { Modal } from '../components/Modal';
import { GenerationProgress } from '../components/GenerationProgress';
import { GalleryStrip } from '../components/GalleryStrip';
import { Icon } from '../components/Icon';
import { UploadCta } from '../components/UploadCta';
import './Video.css';
import '../popups/PickerPopups.css';
import './Photo.css';

const RENDER_MS = 80000;
const RENDER_MESSAGES = ['Creating magic', 'Building your vision', 'Polishing details', 'Almost there'];

// The chosen option shows up as a round thumbnail inside the card (a full-card cover squashed the picture).
function PickerCard({ label, value, img, previews, count, noun, size, onClick }) {
    const picked = Boolean(value);
    return (
        <button type="button" className={`phPicker phPicker--${size}${picked ? ' isPicked' : ''}`} onClick={onClick}>
            <span className="phPickerStack" aria-hidden="true">
                {picked ? (
                    <span className="phPickerPick" key={img}>
                        <img src={img} alt="" />
                        <span className="phPickerCheck"><Icon name="check" size={13} strokeWidth={3.2} /></span>
                    </span>
                ) : (
                    previews.map((src, i) => <img key={src} className={`phPickerPeek phPickerPeek--${i}`} src={src} alt="" />)
                )}
            </span>
            <span className="phPickerInfo">
                {picked && <span className="phPickerLabel">{label}</span>}
                <b>{picked ? value : label}</b>
                <span>{picked ? 'Tap to change' : `${count} ${noun} · Tap to choose`}</span>
            </span>
        </button>
    );
}

function OptionGrid({ options, selected, onSelect, imageMap }) {
    return (
        <div className="pickGrid">
            {options.map((opt, idx) => {
                const src = opt.img || imageMap?.[opt.text];
                return (
                    <button key={opt.text} type="button" className={`pickTile phOption${selected === idx ? ' pickTile--selected' : ''}`} onClick={() => onSelect(idx)}>
                        {src && <img src={src} alt="" loading="lazy" />}
                        <span className="phOptionScrim" />
                        <span className="phOptionText">{opt.text}</span>
                        {selected === idx && <span className="tplCardCheck"><Icon name="check" size={14} strokeWidth={3} /></span>}
                    </button>
                );
            })}
        </div>
    );
}

function ControlRow({ label, options, selected, onSelect }) {
    return (
        <div className="phControl panel">
            <div className="eyebrow">{label}</div>
            <div className="phControlChips">
                {options.map((opt, idx) => (
                    <button key={idx} type="button" className={`chip${selected === idx ? ' chip--active' : ''}`} onClick={() => onSelect(idx)}>
                        {opt.icon && <span aria-hidden="true">{opt.icon}</span>}
                        {opt.text}
                    </button>
                ))}
            </div>
        </div>
    );
}

export function Photo() {
    const { token, user, requireSub } = useSession();

    const [img, setImg] = useState(null);
    const [video, setVideo] = useState(null);
    const [genType, setGenType] = useState(null);
    const [generated, setGenerated] = useState(false);
    const [taskId, setTaskId] = useState(null);

    const [selectedPose, setSelectedPose] = useState(null);
    const [selectedClothes, setSelectedClothes] = useState(null);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [openPopup, setOpenPopup] = useState(null); // 'pose' | 'clothes' | 'location' | null

    const [phase, setPhase] = useState('pick'); // 'pick' | 'choose' | 'girl' | 'generate'
    const [girlStages, setGirlStages] = useState([]);
    const [girlSelections, setGirlSelections] = useState({});
    const [girlName, setGirlName] = useState(null);
    const [gallery, setGallery] = useState([]);

    const formRef = useRef({ type: 'photo', number: 1 });
    const resultRef = useRef(null);

    useEffect(() => {
        if (!token) return;
        fetchGallery([], 0, false, token, false, false, true, 200).then((data) => {
            if (Array.isArray(data?.content)) setGallery(data.content);
        }).catch(() => {});
    }, [token, generated]);

    const { handleGenerate, error } = usePhotoGenerate({
        token, formRef, textPromptActive: false, textPromptValue: '',
        callbacks: {
            setGenType, setGenerated, setImg, setVideo, setTaskId,
            setAnimateAction: () => {}, setGenerationConfig: () => {},
            setShowVideoPopUp: () => {}, setShowPayPopUp: () => {},
            onScrollToResults: () => resultRef.current?.scrollIntoView({ behavior: 'smooth' }),
            onResetInteraction: () => {},
        },
    });

    const pose = selectedPose !== null ? POSE_OPTIONS[selectedPose] : null;
    const ready = selectedPose !== null && selectedClothes !== null && selectedLocation !== null;

    const startCustomGirl = () => requireSub(() => {
        resetFormRef(formRef, pose);
        const first = firstGirlStage(pose);
        if (!first) return;
        setGirlStages([first]);
        setGirlSelections({});
        setGirlName('Custom');
        setPhase('girl');
    });

    const pickPreset = (preset) => requireSub(() => {
        resetFormRef(formRef, pose);
        applyPreset(formRef, pose, preset, selectedClothes, selectedLocation);
        setGirlStages([]);
        setGirlSelections({});
        setGirlName(preset.name);
        setPhase('generate');
    });

    const handleGirlSelect = (stageIndex, optionIndex) => {
        const stage = girlStages[stageIndex];
        formRef.current[stage.name] = optionIndex;
        const selections = { ...girlSelections, [stage.name]: optionIndex };
        const stages = girlStages.slice(0, stageIndex + 1);
        // Answers below the changed stage no longer apply.
        girlStages.slice(stageIndex + 1).forEach((s) => { delete formRef.current[s.name]; delete selections[s.name]; });

        const next = nextGirlStage(formRef, pose, stage.options[optionIndex].next, selectedClothes, selectedLocation);
        if (next) stages.push(next);
        setGirlStages(stages);
        setGirlSelections(selections);
        setPhase(next ? 'girl' : 'generate');
    };

    const handleBack = () => {
        if (phase === 'girl' || phase === 'generate') {
            setPhase('choose');
            setGirlStages([]);
            setGirlSelections({});
            setGirlName(null);
        } else {
            setPhase('pick');
        }
    };

    const handleCreateNew = () => {
        setPhase('pick');
        setSelectedPose(null);
        setSelectedClothes(null);
        setSelectedLocation(null);
        setGirlStages([]);
        setGirlSelections({});
        setGirlName(null);
        setImg(null);
        setVideo(null);
        setGenerated(false);
        setGenType(null);
        formRef.current = { type: 'photo', number: 1 };
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const images = img ? (Array.isArray(img) ? img : [img]) : [];
    const resultUrl = images[0] || video;
    const showLoading = !!genType && !generated;
    const presets = pose ? GIRL_PRESETS.filter((p) => isPresetCompatible(p, pose)) : [];

    const summary = pose && (
        <div className="phSummary">
            <button type="button" className="btn btn--glass btn--sm" onClick={handleBack}><Icon name="arrowLeft" size={15} /> Back</button>
            <div className="phSummaryChips">
                <span className="chip chip--static">{pose.text}</span>
                <span className="chip chip--static">{CLOTHES_OPTIONS[selectedClothes]?.text}</span>
                <span className="chip chip--static">{LOCATION_OPTIONS[selectedLocation]?.text}</span>
                {girlName && phase !== 'choose' && <span className="chip chip--static chip--active">{girlName}</span>}
            </div>
        </div>
    );

    return (
        <div className="phPage">
            {phase === 'pick' && (
                <section className="container phNarrow">
                    <div className="hero">
                        <span className="heroPill"><span className="dot" /> AI photo generator</span>
                        <h1 className="heroTitle">Create <em className="gradText">photo.</em></h1>
                        <p className="heroSub">Pick a pose, an outfit and a place — then design the girl.</p>
                    </div>

                    <div className="phPickers">
                        <PickerCard
                            label="Pose" size="tall" noun="poses" count={POSE_OPTIONS.length} previews={POSE_PREVIEWS}
                            value={pose?.text} img={pose?.img} onClick={() => setOpenPopup('pose')}
                        />
                        <PickerCard
                            label="Clothes" size="wide" noun="styles" count={CLOTHES_OPTIONS.length} previews={CLOTHES_PREVIEWS}
                            value={selectedClothes !== null ? CLOTHES_OPTIONS[selectedClothes].text : null}
                            img={selectedClothes !== null ? CLOTHES_IMAGES[CLOTHES_OPTIONS[selectedClothes].text] : null}
                            onClick={() => setOpenPopup('clothes')}
                        />
                        <PickerCard
                            label="Location" size="wide" noun="places" count={LOCATION_OPTIONS.length} previews={LOCATION_PREVIEWS}
                            value={selectedLocation !== null ? LOCATION_OPTIONS[selectedLocation].text : null}
                            img={selectedLocation !== null ? LOCATION_IMAGES[LOCATION_OPTIONS[selectedLocation].text] : null}
                            onClick={() => setOpenPopup('location')}
                        />
                    </div>

                    <UploadCta
                        label="Add girl"
                        className="phNext"
                        disabled={!ready}
                        onClick={() => requireSub(() => { if (ready) { setPhase('choose'); window.scrollTo({ top: 0, behavior: 'smooth' }); } })}
                    />
                    {!ready && <p className="vidPickHint">Choose a pose, clothes and a location to continue</p>}
                </section>
            )}

            {phase === 'choose' && (
                <section className="container phNarrow">
                    {summary}
                    <button type="button" className="phCustomize glass" onClick={startCustomGirl}>
                        <span className="vidCreateIcon"><Icon name="sparkle" size={24} /></span>
                        <span className="vidCreateText">
                            <b>Customize girl</b>
                            <span>Choose ethnicity, hair, body type and more</span>
                        </span>
                        <Icon name="chevRight" size={20} />
                    </button>

                    {presets.length > 0 && (
                        <>
                            <div className="loginDivider phDivider"><span>or pick a girl</span></div>
                            <div className="phPresets">
                                {presets.map((p) => (
                                    <button key={p.name} type="button" className="phPreset" onClick={() => pickPreset(p)}>
                                        <span className="phPresetAvatar"><img src={p.img} alt="" loading="lazy" /></span>
                                        <span className="phPresetName">{p.name}</span>
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
                </section>
            )}

            {(phase === 'girl' || phase === 'generate') && (
                <section className="container phNarrow">
                    {summary}
                    <div className="phControls">
                        {girlStages.map((stage, idx) => (
                            <ControlRow
                                key={stage.name}
                                label={stage.header}
                                options={stage.options}
                                selected={girlSelections[stage.name] ?? formRef.current[stage.name]}
                                onSelect={(optIdx) => handleGirlSelect(idx, optIdx)}
                            />
                        ))}
                    </div>

                    {phase === 'generate' && !generated && (
                        <button type="button" className="btn btn--primary btn--lg btn--block phNext" onClick={() => requireSub(handleGenerate)} disabled={showLoading}>
                            <Icon name="sparkle" size={17} /> {showLoading ? 'Generating…' : 'Generate'}
                        </button>
                    )}
                    {generated && (
                        <button type="button" className="btn btn--primary btn--lg btn--block phNext" onClick={handleCreateNew}>Create new</button>
                    )}
                </section>
            )}

            <div ref={resultRef} className="vidResultAnchor" />
            {(showLoading || resultUrl || error) && (
                <section className="container vidResult">
                    {showLoading && (
                        <GenerationProgress durationMs={RENDER_MS} title={girlName} messages={RENDER_MESSAGES} tip="Don't close this tab — your photo is rendering" />
                    )}
                    {!showLoading && resultUrl && (
                        <div className="vidResultCard glass">
                            <div className="vidResultHead">
                                <span className="heroPill"><span className="dot dot--success" /> Ready</span>
                                <span className="sectionMeta">{girlName || 'Photo'} · just now</span>
                            </div>
                            {images.length > 0
                                ? <img className="vidResultVideo" src={images[0]} alt="Generated" />
                                : <video className="vidResultVideo" src={video} autoPlay loop muted playsInline />}
                            <div className="vidResultActions">
                                <button type="button" className="btn btn--primary" onClick={() => downloadFile(resultUrl)}>
                                    <Icon name="download" size={16} /> Download
                                </button>
                                <Link className="btn btn--glass" to="/"><Icon name="video" size={16} /> Make a video</Link>
                                <button type="button" className="btn btn--glass" onClick={handleCreateNew}><Icon name="plus" size={16} /> Create another</button>
                            </div>
                            {taskId && (
                                <Link className="vidReportLink" to={`/report?media_id=${encodeURIComponent(taskId)}`}>
                                    <Icon name="flag" size={13} /> Report content
                                </Link>
                            )}
                        </div>
                    )}
                    {error && <div className="note note--error phError">{error}</div>}
                </section>
            )}

            {user && <GalleryStrip items={gallery} token={token} kind="photo" emptyHint="Compose a shot above to get started" />}

            <Modal isOpen={openPopup === 'pose'} onClose={() => setOpenPopup(null)} size="lg" title="Choose pose">
                <OptionGrid options={POSE_OPTIONS} selected={selectedPose} onSelect={(i) => { setSelectedPose(i); setOpenPopup(null); }} />
            </Modal>
            <Modal isOpen={openPopup === 'clothes'} onClose={() => setOpenPopup(null)} size="lg" title="Choose clothes">
                <OptionGrid options={CLOTHES_OPTIONS} imageMap={CLOTHES_IMAGES} selected={selectedClothes} onSelect={(i) => { setSelectedClothes(i); setOpenPopup(null); }} />
            </Modal>
            <Modal isOpen={openPopup === 'location'} onClose={() => setOpenPopup(null)} size="lg" title="Choose location">
                <OptionGrid options={LOCATION_OPTIONS} imageMap={LOCATION_IMAGES} selected={selectedLocation} onSelect={(i) => { setSelectedLocation(i); setOpenPopup(null); }} />
            </Modal>
        </div>
    );
}
